import React, { useState } from "react";
import { Modal, Spin, DatePicker, Table } from "antd";
import PropTypes from "prop-types";
import { reactivateEmployee } from '../../../service/employeeResignService';
import Swal from "sweetalert2";
import { X } from "lucide-react";
import dayjs from "dayjs";

const EmployeeReactivateModal = ({ isOpen, onClose, empRecord, recordId, resignData, onReactivateSuccess }) => {
    const [reactivationReason, setReactivationReason] = useState('');
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);
    const [reactivationDate, setReactivationDate] = useState(null);
    const [selectedResignation, setSelectedResignation] = useState(null);

    const handleReset = () => {
        setReactivationReason("");
        setRemarks("");
        setReactivationDate(null);
        setSelectedResignation(null);
    };

    const showWarning = (str) => {
        return Swal.fire({
            title: "Error",
            text: str,
            icon: "error",
        });
    };

    const handleSubmit = async () => {
        if (!reactivationDate) {
            showWarning("Please select a reactivation date");
            return;
        }
        if (!reactivationReason.trim()) {
            showWarning("Please enter a reactivation reason");
            return;
        }
        if (!selectedResignation) {
            showWarning("Please select a resignation record");
            return;
        }

        const formattedDate = reactivationDate.format('YYYY-MM-DD');

        try {
            const result = await reactivateEmployee(
                recordId,
                selectedResignation._id,
                formattedDate,
                reactivationReason,
                remarks,
                setLoading
            );
            
            console.log(result);
            handleReset();
            
            Swal.fire({
                title: "Success",
                text: "Employee reactivated successfully",
                icon: "success",
            });
            
            // Call success callback to refresh parent data
            if (onReactivateSuccess) {
                onReactivateSuccess();
            }
            
            onClose();
        } catch (error) {
            setLoading(false);
            console.error('Error during reactivation:', error);
            showWarning(error.response?.data?.message || "Failed to reactivate employee");
        }
    };

    // Columns for resignation records table
    const columns = [
        {
            title: 'Select',
            dataIndex: 'select',
            key: 'select',
            width: 70,
            render: (_, record) => (
                <input
                    type="radio"
                    name="resignation"
                    checked={selectedResignation?._id === record._id}
                    onChange={() => setSelectedResignation(record)}
                />
            ),
        },
        {
            title: 'Resign Date',
            dataIndex: 'resignDate',
            key: 'resignDate',
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
    ];

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            width="60vw"
            footer={null}
            maskClosable={false}
            closable={false}
            zIndex={1200}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <div className="page-title">
                    <h4>Reactivate Employee</h4>
                    <p className="mb-0">Reactivate a resigned employee</p>
                </div>
                <X
                    style={{
                        fontSize: "20px",
                        color: "#ff9500",
                        cursor: "pointer",
                    }}
                    onClick={onClose}
                />
            </div>
            
            <div className="modal-body">
                <form>
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="employeeName">Employee Name</label>
                                <input
                                    type="text"
                                    id="employeeName"
                                    className="form-control"
                                    value={empRecord?.full_name || `${empRecord?.first_name || ''} ${empRecord?.last_name || ''}`}
                                    disabled
                                />
                            </div>
                        </div>
                        
                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="currentStatus">Current Status</label>
                                <input
                                    type="text"
                                    id="currentStatus"
                                    className="form-control"
                                    value={empRecord?.status || 'N/A'}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Select Resignation Record</label>
                            <Table
                                columns={columns}
                                dataSource={resignData}
                                rowKey="_id"
                                pagination={false}
                                size="small"
                                scroll={{ y: 200 }}
                                bordered
                            />
                        </div>

                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="reactivationDate">Reactivation Date</label>
                                <DatePicker
                                    value={reactivationDate}
                                    onChange={(date) => setReactivationDate(date)}
                                    className="form-control"
                                    placeholder="Select reactivation date"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="reactivationReason">Reactivation Reason *</label>
                                <textarea
                                    value={reactivationReason || ''}
                                    id="reactivationReason"
                                    className="form-control"
                                    rows="3"
                                    placeholder="Enter the reason for reactivation"
                                    onChange={(e) => setReactivationReason(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="remarks">Remarks (Optional)</label>
                                <textarea
                                    value={remarks || ''}
                                    id="remarks"
                                    className="form-control"
                                    rows="2"
                                    placeholder="Enter any additional remarks"
                                    onChange={(e) => setRemarks(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </form>
                
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "20px",
                    }}
                >
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => {
                            onClose();
                            handleReset();
                        }}
                    >
                        Close
                    </button>
                    <div>
                        <button
                            type="button"
                            className="btn btn-reset me-2"
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                        <button 
                            className="btn btn-submit" 
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? <Spin size="small" /> : "Reactivate"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

EmployeeReactivateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    empRecord: PropTypes.object.isRequired,
    recordId: PropTypes.string.isRequired,
    resignData: PropTypes.array,
    onReactivateSuccess: PropTypes.func,
};

export default EmployeeReactivateModal;
